import { useState } from 'react'
import { Loader2, Save } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import ScopePanel from '@/components/ScopePanel'
import { useAppStore } from '@/store'
import type { ConnectorRecord } from '@/types/connector'

type ScopeModalProps = {
  isOpen: boolean
  onClose: () => void
  connector: ConnectorRecord | null
}

// AF Discovery lives here (a per-row action), NOT in the create/edit modal — discovery
// needs a LIVE connector to enumerate the customer's AF (the cloud can't reach PI). The
// chosen scope is merged into the connector's config and persisted via updateConnector,
// so it hot-applies on the connector's next heartbeat.
const ScopeModal = ({ isOpen, onClose, connector }: ScopeModalProps) => {
  const { t } = useTranslation()
  const { updateConnector, loading } = useAppStore()
  const [scope, setScope] = useState<Record<string, unknown>>({})
  const [discovering, setDiscovering] = useState(false)

  if (!connector) return null

  const handleSave = async () => {
    try {
      await updateConnector(connector.id, {
        config: { ...connector.config, ...scope },
      })
      onClose()
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `${t('connectors:errors.update')}: ${error.message}`
          : t('connectors:errors.update')
      )
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('connectors:scope.title')} — ${connector.name}`}
      className="w-full max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={discovering}>
            {t('common:buttons.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={loading || discovering}>
            {loading ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Save className="w-5 h-5 mr-2" />
            )}
            {t('connectors:scope.save')}
          </Button>
        </div>
      }
    >
      <div className="py-2">
        <ScopePanel
          connector={connector}
          onScopeChange={setScope}
          onDiscoveringChange={setDiscovering}
        />
      </div>
    </Modal>
  )
}

export default ScopeModal
